/** Parse an option description like "CHEWY INC 04/24/2026 $31 Call" or "AAPL 04/10/2026 230.00 C" into strike and expiration. */
export function parseOptionDescription(desc: string): { strike: number; expiration?: string } {
  // Find MM/DD/YYYY anywhere in the string
  const dm = desc.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  const expiration = dm ? `${dm[3]}-${dm[1]}-${dm[2]}` : undefined;
  // Find strike: "$31" or "$22.5" or bare "230.00" before Call/Put/C/P
  const sm = desc.match(/\$?([\d.]+)\s+(?:Call|Put|C|P)\b/i);
  const strike = sm ? parseFloat(sm[1]) || 0 : 0;
  return { strike, expiration };
}

/**
 * OCC root prefix ("RUT", "RUTW"). Not the tradeable underlying.
 * Friday RUT monthlies and RUTW weeklies are different listed contracts.
 */
export function occRootOf(optionSymbol: string): string {
  const trimmed = optionSymbol.trim();
  const m = trimmed.match(/^([A-Za-z]+)/);
  return m ? m[1].toUpperCase() : "";
}

/** Weekly (or PM-settled weekly) *index* OCC root: RUTW/SPXW/NDXP/BKXPM.
 *  Not equity tickers that happen to end in W/PM (JPM, LOW). */
const INDEX_WEEKLY_ROOTS = new Set([
  "SPXW", "SPXPM", "RUTW", "RUTPM", "NDXP", "NDXPM", "DJXW", "VIXW", "XSPW",
  "OEXW", "XEOW", "BKXPM", "OSXPM",
]);
export function isWeeklyOccRoot(root: string): boolean {
  return INDEX_WEEKLY_ROOTS.has(root.trim().toUpperCase());
}

/** `weeklies` | `monthlies` | `both` — which listed chains to keep in a VS/0DTE scan. */
export function keepOccForIndexExpirations(optionSymbol: string, mode: string | undefined): boolean {
  if (mode !== "weeklies" && mode !== "monthlies") return true;
  const root = occRootOf(optionSymbol);
  if (!root) return true;
  const weekly = isWeeklyOccRoot(root);
  return mode === "weeklies" ? weekly : !weekly;
}

/** Full OCC symbol with whitespace removed. Roots stay distinct. */
export function occIdentity(optionSymbol: string): string {
  return optionSymbol.replace(/\s+/g, "").toUpperCase();
}

/** Working close (stop, take-profit, or abandon) on this vertical. */
export function isWorkingVsCloseOrder(
  o: { orderType: string; optionSymbol: string; longOptionSymbol?: string | null },
  shortOcc: string,
  longOcc?: string | null,
): boolean {
  if (occIdentity(o.optionSymbol) !== occIdentity(shortOcc)) return false;
  if (o.orderType === "BUY_TO_CLOSE") return true;
  if (o.orderType !== "VERTICAL_SPREAD_CLOSE") return false;
  if (!longOcc || !o.longOptionSymbol) return true;
  return occIdentity(o.longOptionSymbol) === occIdentity(longOcc);
}

/**
 * Canonical listed-contract identity: tradeable underlying + full OCC symbol
 * (root + date + P/C + strike), whitespace-stripped.
 * RUT 260821P03000000 and RUTW 260821P03000000 are different contracts.
 */
export function contractKeyOf(underlying: string, optionSymbol: string): string {
  return `${underlying.replace(/^\$/, "")}|${occIdentity(optionSymbol)}`;
}

/** Calendar date (YYYY-MM-DD) in America/New_York. */
function etYmd(d = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function etHourMinute(d = new Date()): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const num = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  return { hour: num("hour"), minute: num("minute") };
}

/**
 * Time-to-expiry in years-of-days for Black-Scholes on an open position.
 * Multi-DTE uses calendar days (same as the scan). Same-day expiry uses the
 * remaining cash session until 16:00 ET, floored at 15 minutes so IV still
 * inverts after the close bell is close.
 */
export function daysToExpirationBs(expirationDate: string): number {
  const today = etYmd();
  const [ey, em, ed] = expirationDate.split("-").map(Number);
  const [ty, tm, td] = today.split("-").map(Number);
  const calDays = Math.round(
    (Date.UTC(ey, em - 1, ed) - Date.UTC(ty, tm - 1, td)) / 86_400_000,
  );
  if (calDays > 0) return calDays;
  const { hour, minute } = etHourMinute();
  const leftMin = Math.max(16 * 60 - (hour * 60 + minute), 15);
  return leftMin / (24 * 60);
}

/**
 * Parse the structural fields of an OCC option symbol like
 * "XSP   260418P00627000". The OCC root (the alphabetic prefix, e.g. "XSP",
 * "RUTW") is intentionally NOT returned: it is NOT the tradeable underlying
 * ticker, and every caller must plumb the underlying from whoever knows it
 * (the order/position response's `underlying`/`underlyingSymbol`, or the
 * vs_trades / cc trade row). See feedback_no_occ_prefix_underlying.
 */
export function parseOptionSymbol(sym: string): {
  expDate: string; putCall: "P" | "C"; strike: number;
} | null {
  // OCC format: SYMBOL (padded to 6) + YYMMDD + P/C + strike*1000 (8 digits)
  const m = sym.match(/^.+?\s*(\d{6})([PC])(\d{8})$/);
  if (!m) return null;
  const yy = m[1].slice(0, 2), mm = m[1].slice(2, 4), dd = m[1].slice(4, 6);
  const expDate = `20${yy}-${mm}-${dd}`;
  const putCall = m[2] as "P" | "C";
  const strike = parseInt(m[3], 10) / 1000;
  return { expDate, putCall, strike };
}

/** Strike + expiration from a Schwab-style description, else the OCC symbol. */
export function optionStrikeExpiration(optionSymbol: string, description?: string): {
  strike: number; expiration: string;
} {
  const d = parseOptionDescription(description || "");
  if (d.strike > 0 && d.expiration) return { strike: d.strike, expiration: d.expiration };
  const occ = parseOptionSymbol(optionSymbol);
  if (occ) return { strike: occ.strike, expiration: occ.expDate };
  return { strike: d.strike || 0, expiration: d.expiration || "" };
}

/**
 * Cash/index ticker from a broker `underlyingSymbol` (RUTW → RUT, $RUT → RUT).
 * Uses the broker field, not the OCC root of the option symbol.
 */
export function cashTicker(underlying: string): string {
  let s = underlying.trim().replace(/^\$/, "").toUpperCase();
  if (s === "NDXP") return "NDX";
  if (s.endsWith("PM") && s.length > 2 && isWeeklyOccRoot(s)) s = s.slice(0, -2);
  else if (s.endsWith("W") && s.length > 1 && isWeeklyOccRoot(s)) s = s.slice(0, -1);
  return s;
}
